import {
  TEAM_MEMBER_ROLE_LABELS,
  teamShirtsSchema,
  type AdminTeam,
  type TeamShirtPlayer,
  type TeamShirts,
} from '@growup/site-shared';
import { Button, Card, Field, Notice, Select, StatusBadge } from '@growup/ui';
import { useEffect, useState } from 'react';

import { getJson, messageOf, send } from '../api';
import { GIFT_STATE_LOOK } from '../members/StreamerShirt';

const BASE = '/api/site/teams';

const dayFormat = new Intl.DateTimeFormat('pt-PT', {
  timeZone: 'Europe/Lisbon',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});
const dayOf = (iso: string) => dayFormat.format(new Date(iso));

/**
 * Camisola da equipa (parte 9, docs/equipas, RF-EQ15): o mesmo sistema da
 * camisola de streamer. O Gestor de Equipas escolhe o produto; o Gestor ou o
 * Manager gera um codigo de oferta por pessoa do lineup, que vai por email.
 */
export function ShirtsCard({ team, canChoose }: { team: AdminTeam; canChoose: boolean }) {
  const [shirts, setShirts] = useState<TeamShirts | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const archived = team.status === 'archived';
  const path = `${BASE}/${team.id}/shirts`;

  useEffect(() => {
    getJson(path, teamShirtsSchema)
      .then(setShirts)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [path]);

  const run = async (method: string, url: string, body?: unknown) => {
    setBusy(true);
    setError(null);
    try {
      setShirts(teamShirtsSchema.parse(await send(method, url, body)));
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  if (shirts === null) {
    return (
      <Card headingLevel={2} title="Camisola da equipa">
        {error ? <Notice tone="danger">{error}</Notice> : <p>A carregar…</p>}
      </Card>
    );
  }

  const missing = shirts.players.filter((p) => p.gift === null).length;
  const row = (p: TeamShirtPlayer, canOffer: boolean) => (
    <li key={p.accountId} className="flex flex-wrap items-center gap-2 py-2">
      <div className="flex min-w-0 basis-full flex-col sm:basis-0 sm:flex-1">
        <span className="font-semibold">
          {p.login}
          <span className="ml-2 text-caption font-normal text-gu-ink-muted">
            {TEAM_MEMBER_ROLE_LABELS[p.role]}
            {p.youth ? ' · jovem (vai para o encarregado)' : ''}
          </span>
        </span>
        {p.gift ? (
          <span className="text-caption text-gu-ink-muted">
            {p.gift.code} · gerado a {dayOf(p.gift.createdAt)}
            {p.gift.createdBy ? ` por ${p.gift.createdBy}` : ''} · vale até{' '}
            {dayOf(p.gift.expiresAt)}
            {p.gift.orderNumber ? ` · encomenda ${p.gift.orderNumber}` : ''}
          </span>
        ) : null}
      </div>
      {p.gift ? (
        <StatusBadge tone={GIFT_STATE_LOOK[p.gift.state].tone}>
          {GIFT_STATE_LOOK[p.gift.state].label}
        </StatusBadge>
      ) : canOffer ? (
        <Button
          size="sm"
          variant="secondary"
          disabled={busy || shirts.product === null}
          aria-label={`Oferecer a camisola a ${p.login}`}
          onClick={() => void run('POST', `${path}/${p.accountId}`)}
        >
          Oferecer
        </Button>
      ) : (
        <StatusBadge tone="neutral">Sem código</StatusBadge>
      )}
    </li>
  );

  return (
    <Card headingLevel={2} title="Camisola da equipa">
      <div className="flex flex-col gap-4">
        <p className="text-gu-ink-muted">
          Cada pessoa do lineup recebe por email um código de oferta que vale uma unidade da
          camisola, em qualquer tamanho (nome e número pagam-se à parte). O de um jovem vai para o
          encarregado, que o usa na sua conta. Também serve ao balcão.
        </p>
        {canChoose ? (
          <Field
            label="Produto da camisola"
            hint="Pode estar fora da loja: quem tem o código escolhe-o no carrinho."
          >
            <Select
              value={shirts.product?.id ?? ''}
              disabled={busy}
              onChange={(e) =>
                void run('PUT', `${path}/product`, { productId: e.target.value || null })
              }
            >
              <option value="">Por escolher</option>
              {shirts.products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.active ? p.name : `${p.name} (fora da loja)`}
                </option>
              ))}
            </Select>
          </Field>
        ) : shirts.product ? (
          <p>
            Camisola: <span className="font-semibold">{shirts.product.name}</span>
          </p>
        ) : null}
        {shirts.product === null ? (
          <Notice tone="warning">
            {canChoose
              ? 'Escolha o produto da camisola para poder gerar os códigos.'
              : 'O Gestor de Equipas ainda não escolheu a camisola desta equipa.'}
          </Notice>
        ) : null}
        {error ? <Notice tone="danger">{error}</Notice> : null}
        {shirts.players.length === 0 ? <p>Ainda ninguém no lineup.</p> : null}
        <ul className="flex flex-col divide-y divide-gu-line">
          {shirts.players.map((p) => row(p, !archived))}
        </ul>
        {!archived && missing > 0 && shirts.product !== null ? (
          <div>
            <Button
              size="sm"
              disabled={busy}
              onClick={() => {
                if (
                  window.confirm(
                    `Gerar e enviar ${String(missing)} ${missing === 1 ? 'código' : 'códigos'} da camisola?`,
                  )
                )
                  void run('POST', path);
              }}
            >
              {missing === 1
                ? 'Oferecer a quem falta (1)'
                : `Oferecer a quem falta (${String(missing)})`}
            </Button>
          </div>
        ) : null}
        {shirts.former.length > 0 ? (
          <div className="flex flex-col gap-1">
            <h3 className="font-sub text-label font-bold uppercase text-gu-ink-muted">
              Quem já saiu (o código continua válido)
            </h3>
            <ul className="flex flex-col divide-y divide-gu-line">
              {shirts.former.map((p) => row(p, false))}
            </ul>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
